import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const UserDefineContext = createContext();

export const UserDefineProvider = ({ children, connectionString }) => {
    const { selectedCompany } = useAuth();

    const [userDefineData, setUserDefineData] = useState({});
    const [userDefineLoading, setUserDefineLoading] = useState(false);

    const fetchUserDefineData = useCallback(async (companyId) => {
        if (!companyId) return;
        setUserDefineLoading(true);
        try {
            const res = await axios.get(`${connectionString}/api/mastersalegrid`, {
                params: { companyId }
            });
            console.log('mastersalegrid:',res.data);
            setUserDefineData(res.data || {});
        } catch (err) {
            console.error('mastersalegrid fetch error:', err);
            setUserDefineData({});
        } finally {
            setUserDefineLoading(false);
        }
    }, [connectionString]);

    // AuthContext dispatches this ~2.8s after user + selectedCompany are ready
    // (see the 'userDefineTrigger' useEffect in AuthContext.js)
    useEffect(() => {
        const handler = (e) => {
            const companyId = e.detail?.companyId;
            if (companyId) fetchUserDefineData(companyId);
        };
        window.addEventListener('userDefineTrigger', handler);
        return () => window.removeEventListener('userDefineTrigger', handler);
    }, [fetchUserDefineData]);

    useEffect(() => {
        console.log('userDefineData:', userDefineData);
    }, [userDefineData]);

    // Additional Charges (userdefinecharges) are now GLOBAL (not per-BookName)
    // They arrive as a flat array under userDefineData.additionalCharges
    // (see MasterSaleGridController.getMasterSaleGrid)
    const getAdditionalCharges = useCallback(() => {
        return userDefineData?.additionalCharges || [];
    }, [userDefineData]);

    // Only the charge slots whose checkbox (ColToBeAsk) was selected when saved
    const getSelectedCharges = useCallback(() => {
        return getAdditionalCharges().filter(r => Boolean(r.ColToBeAsk));
    }, [getAdditionalCharges]);

    // Get one charge slot's full row by its fixed position (SrNo, 1-5)
    const getChargeRow = useCallback((srNo) => {
        return getAdditionalCharges().find(r => Number(r.SrNo) === srNo);
    }, [getAdditionalCharges]);

    // The charge's linked productmaster row (the fixed charge-item created for
    // this slot's TransType, see createDefaultChargeItems / MasterSaleGridController)
    // — already present as ItemId on every row from getAdditionalCharges/getChargeRow,
    // this is just a convenience accessor by SrNo.
    const getChargeItemId = useCallback((srNo) => {
        return getChargeRow(srNo)?.ItemId ?? null;
    }, [getChargeRow]);

    // Memoize refetch callback separately to maintain stable reference
    const refetchUserDefineData = useCallback(() => {
        fetchUserDefineData(selectedCompany?.CompId);
    }, [fetchUserDefineData, selectedCompany?.CompId]);

    // Memoize context value to prevent unnecessary re-renders of entire app tree
    const contextValue = useMemo(() => ({
        userDefineData,
        userDefineLoading,
        refetchUserDefineData,
        getAdditionalCharges,
        getSelectedCharges,
        getChargeRow,
        getChargeItemId,
    }), [
        userDefineData,
        userDefineLoading,
        refetchUserDefineData,
        getAdditionalCharges,
        getSelectedCharges,
        getChargeRow,
        getChargeItemId,
    ]);

    return (
        <UserDefineContext.Provider value={contextValue}>
            {children}
        </UserDefineContext.Provider>
    );
};

export const useUserDefine = () => useContext(UserDefineContext);
